import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { loadView, viewQuery } from "@/api/views";
import { brandHead } from "@/lib/head";
import CustomerPage from "@/pages/app/customers/[id]/page";
import CustomerNewPage from "@/pages/app/customers/new/page";
import CustomersPage from "@/pages/app/customers/page";
import InsightsPage from "@/pages/app/insights/page";
import InsightsReportPage from "@/pages/app/insights/report/page";

import { appRoute, APP_BOUNDARIES } from "./w0b";

interface CustomersSearch {
  q?: string;
  stage?: string;
  assignee?: string;
  view?: string;
  col?: string;
  all?: string[];
  page?: string;
}

// 주소의 쿼리는 사람이 고칠 수 있다 — 글자·숫자만 문자열로 받고 나머지는 버린다(값 판정은 core 가 한다).
const textOf = (value: unknown): string | undefined =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined;

// ?all= 은 한 값(쉼표 묶음)이나 여러 값 — 문자열 배열로 모은다.
const textsOf = (value: unknown): string[] | undefined => {
  const list = (Array.isArray(value) ? value : [value]).map(textOf).filter((v): v is string => v !== undefined);
  return list.length ? list : undefined;
};

const customersQuery = (search: CustomersSearch) => viewQuery("customers", search);
const customerQuery = (id: string, site: string | undefined) => viewQuery("customer", { id, site });

const CustomersRouteComponent = () => {
  const search = customersRoute.useSearch();
  const { data } = useSuspenseQuery(customersQuery(search));
  return <CustomersPage data={data} />;
};

export const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers",
  validateSearch: (search: Record<string, unknown>): CustomersSearch => ({
    q: textOf(search.q),
    stage: textOf(search.stage),
    assignee: textOf(search.assignee),
    view: textOf(search.view),
    col: textOf(search.col),
    all: textsOf(search.all),
    page: textOf(search.page),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, customersQuery(deps)),
  head: () => brandHead("고객"),
  component: CustomersRouteComponent,
  ...APP_BOUNDARIES,
});

const CustomerNewRouteComponent = () => {
  const { data } = useSuspenseQuery(viewQuery("customer-new"));
  return <CustomerNewPage data={data} />;
};

export const customerNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers/new",
  loader: ({ context }) => loadView(context.queryClient, viewQuery("customer-new")),
  head: () => brandHead("고객 등록"),
  component: CustomerNewRouteComponent,
  ...APP_BOUNDARIES,
});

const CustomerRouteComponent = () => {
  const { id } = customerRoute.useParams();
  const { site } = customerRoute.useSearch();
  const { data } = useSuspenseQuery(customerQuery(id, site));
  return <CustomerPage data={data} />;
};

export const customerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers/$id",
  validateSearch: (search: Record<string, unknown>): { site?: string } => ({ site: textOf(search.site) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => loadView(context.queryClient, customerQuery(params.id, deps.site)),
  // 제목은 읽어 둔 고객 이름 — 없으면 「고객」.
  head: ({ match, params }) => brandHead(match.context.queryClient.getQueryData(customerQuery(params.id, match.search.site).queryKey)?.title ?? "고객"),
  component: CustomerRouteComponent,
  ...APP_BOUNDARIES,
});

const InsightsRouteComponent = () => {
  const search = insightsRoute.useSearch();
  const { data } = useSuspenseQuery(viewQuery("insights", search));
  return <InsightsPage data={data} />;
};

export const insightsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/insights",
  validateSearch: (search: Record<string, unknown>): { range?: string } => ({ range: textOf(search.range) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, viewQuery("insights", deps)),
  head: () => brandHead("유입 분석"),
  component: InsightsRouteComponent,
  ...APP_BOUNDARIES,
});

const InsightsReportRouteComponent = () => {
  const search = insightsReportRoute.useSearch();
  const { data } = useSuspenseQuery(viewQuery("insights-report", search));
  return <InsightsReportPage data={data} />;
};

export const insightsReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/insights/report",
  validateSearch: (search: Record<string, unknown>): { month?: string } => ({ month: textOf(search.month) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, viewQuery("insights-report", deps)),
  head: () => brandHead("월간 리포트"),
  component: InsightsReportRouteComponent,
  ...APP_BOUNDARIES,
});

export const routes: AnyRoute[] = [customersRoute, customerNewRoute, customerRoute, insightsRoute, insightsReportRoute];
